import { createAsyncAction } from 'async-selector-kit'
import cloneDeep from 'lodash/cloneDeep'
import { SelectUnit } from '../../SelectUnits/types'
import { getUnitsIndividual, getUnitsMaster } from '../selectors'
import { RootState } from '../../../store'
import { UNITS_MASTER } from '../../../firebase/config'
import { UnitMaster } from '../reducer'
import shortid from 'shortid'
import { setIndividualUnitFirebase } from './individual'
import { onError } from '../../Profile/selectors/api/utils'

const updateMasterUnitsFirebase = async (model: UnitMaster) =>
  window.firebase
    .database()
    .ref()
    .child(`${UNITS_MASTER}/${model.id}`)
    .update(model)

/** Input shape for normalization; Firebase/forms may send numeric fields as strings. */
type UnitMasterInput = Omit<UnitMaster, 'quantity' | 'price' | 'occupied'> & {
  quantity?: number | string
  price?: number | string
  occupied?: number | string
}

function toNumber(v: number | string | undefined, fallback: number): number {
  if (v == null || v === '') return fallback
  return typeof v === 'string' ? parseInt(String(v), 10) : v
}

/** Normalize master fields that may come as strings from Firebase/forms. Exported for testing. */
export function normalizeMasterForFirebase(model: UnitMasterInput): UnitMaster {
  const m = cloneDeep(model) as UnitMaster
  m.quantity = toNumber(m.quantity, 0)
  m.price = toNumber(m.price, 0)
  m.occupied = toNumber(m.occupied, 0)
  return m
}

export type StoreLike = { getState: () => RootState; dispatch: (action: unknown) => void }

/**
 * Core logic: create or update master in Firebase; when new, create N individual slots by id.
 * Exported so cancelRequestUnitMaster can await it.
 */
export async function executeUpdateFirebaseMaster(model: UnitMaster): Promise<void> {
  const normalized = normalizeMasterForFirebase(model)
  const id = normalized.id ?? shortid.generate()
  const isUpdate = Boolean(normalized.id)
  const payload: UnitMaster = { ...normalized, id }

  try {
    await updateMasterUnitsFirebase(payload)
  } catch (error) {
    onError('failed to update firebase')
    return
  }

  if (!isUpdate) {
    const individualPromises = Array.from({ length: payload.quantity }, () =>
      setIndividualUnitFirebase({
        id: shortid.generate(),
        masterId: payload.id,
        size: payload.size,
        isClimate: payload.isClimate,
        isCovered: payload.isCovered,
        unitNumber: '',
      })
    )
    await Promise.all(individualPromises)
  }
}

/**
 * Core logic: find master by id, increment occupied, write to Firebase. Exported for testing.
 */
export async function executeUpdateUnitMaster(
  store: StoreLike,
  masterId: string
): Promise<void> {
  const masterUnits = getUnitsMaster(store.getState() as RootState)
  const objMaster = masterUnits.find((y) => y.id === masterId)
  if (objMaster == null) return

  const updated = cloneDeep(objMaster)
  updated.occupied = (updated.occupied ?? 0) + 1
  try {
    await updateMasterUnitsFirebase(updated)
  } catch (error) {
    onError('failed to update firebase')
  }
}

/**
 * Core logic: find individual and its master, decrement master occupied, persist. Exported for testing.
 */
export async function executeCancelRequestUnitMaster(
  store: StoreLike,
  unit: SelectUnit
): Promise<void> {
  const individualUnits = getUnitsIndividual(store.getState() as RootState)
  const masterUnits = getUnitsMaster(store.getState() as RootState)

  const _unit = individualUnits.find(
    (y) =>
      y.subscriptionId === unit.subscriptionId && y.customerProfileId === unit.customerProfileId
  )
  if (_unit == null) return

  const mUnit = masterUnits.find(
    (y) =>
      y.size === _unit.size &&
      y.isClimate === _unit.isClimate &&
      y.isCovered === _unit.isCovered
  )
  if (mUnit == null) return

  const updated = cloneDeep(mUnit)
  updated.occupied -= 1
  await executeUpdateFirebaseMaster(updated)
}

export const [
  updateUnitMaster,
  updateUnitMasterLoading,
  updateUnitMasterError,
] = createAsyncAction(
  {
    id: 'update-unit-master',
    async: (store) => async (masterId: string) => {
      await executeUpdateUnitMaster(store, masterId)
    },
  },
  []
)

export const [
  cancelRequestUnitMaster,
  cancelRequestUnitMasterLoading,
  cancelRequestUnitMasterError,
] = createAsyncAction(
  {
    id: 'cancel-request-unit-master',
    async: (store) => async (unit: SelectUnit) => {
      await executeCancelRequestUnitMaster(store, unit)
    },
  },
  []
)
